/**
 * LegacyNoteImport.jsx
 * 
 * Admin tool for importing notes from legacy EMR text file exports.
 * Supports bulk upload, preview, and import with detailed reporting.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  Upload, FileText, CheckCircle, XCircle, AlertCircle, 
  Download, Loader2, Users, Calendar, Stethoscope,
  FileQuestion, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '../ui/collapsible';
import { toast } from 'sonner';
import { getAuthToken } from '../../utils/authHelpers';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LegacyNoteImport = () => {
  const [files, setFiles] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('upload'); // upload, preview, results
  const [expandedItems, setExpandedItems] = useState({});
  const [clientSelections, setClientSelections] = useState({}); // filename -> selected client_id
  const [updateExisting, setUpdateExisting] = useState(false); // Global: Update all duplicate notes
  const [duplicateActions, setDuplicateActions] = useState({}); // Per-file: filename -> 'update' | 'skip'
  const importInProgressRef = useRef(false); // Ref-based guard against double-clicks

  const MAX_FILES_PER_UPLOAD = 5000;  // Maximum files per batch upload
  
  const onDrop = useCallback((acceptedFiles) => {
    // Filter for .txt files
    const txtFiles = acceptedFiles.filter(f => 
      f.name.toLowerCase().endsWith('.txt')
    );
    
    if (txtFiles.length !== acceptedFiles.length) {
      toast.warning('Some files were skipped - only .txt files are supported');
    }
    
    // Check total file count
    const newTotal = files.length + txtFiles.length;
    if (newTotal > MAX_FILES_PER_UPLOAD) {
      const canAdd = MAX_FILES_PER_UPLOAD - files.length;
      if (canAdd > 0) {
        setFiles(prev => [...prev, ...txtFiles.slice(0, canAdd)]);
        toast.warning(`Added ${canAdd} files. Maximum is ${MAX_FILES_PER_UPLOAD} files per batch.`);
      } else {
        toast.error(`Maximum ${MAX_FILES_PER_UPLOAD} files per batch. Remove some files first.`);
      }
      return;
    }
    
    setFiles(prev => [...prev, ...txtFiles]);
  }, [files.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt']
    },
    multiple: true,
    maxFiles: MAX_FILES_PER_UPLOAD
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setPreviewData(null);
    setImportResults(null);
    setClientSelections({});
    setUpdateExisting(false);
    setDuplicateActions({});
    setStep('upload');
  };

  const handlePreview = async () => {
    if (files.length === 0) {
      toast.error('Please add files first');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const token = getAuthToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      const response = await axios.post(
        `${API}/api/legacy-import/preview`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setPreviewData(response.data);
      setStep('preview');
      toast.success(`Parsed ${response.data.total_files} files`);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse files');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData) return;
    if (importInProgressRef.current) return; // Prevent double-click

    // Count how many will be imported (matched + user-selected)
    const readyCount = previewData.parsed.filter(p => 
      p.parsed_successfully && (p.matched_client || clientSelections[p.filename])
    ).length;

    if (readyCount === 0) {
      toast.error('No notes are ready for import (all missing client matches)');
      return;
    }

    importInProgressRef.current = true;
    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add client selections as JSON
      if (Object.keys(clientSelections).length > 0) {
        formData.append('client_overrides', JSON.stringify(clientSelections));
      }
      
      // Add per-file duplicate actions (files marked for update)
      const filesToUpdate = Object.entries(duplicateActions)
        .filter(([_, action]) => action === 'update')
        .map(([filename]) => filename);
      
      if (filesToUpdate.length > 0) {
        formData.append('files_to_update', JSON.stringify(filesToUpdate));
      }

      const token = getAuthToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        `${API}/api/legacy-import/execute`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResults(response.data);
      setStep('results');
      const importedCount = response.data.imported || 0;
      const updatedCount = response.data.updated || 0;
      if (updatedCount > 0) {
        toast.success(`Imported ${importedCount} new notes, updated ${updatedCount} existing notes`);
      } else {
        toast.success(`Imported ${importedCount} notes`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import notes');
    } finally {
      setLoading(false);
      importInProgressRef.current = false;
    }
  };

  const handleSelectClient = (filename, clientId) => {
    setClientSelections(prev => ({
      ...prev,
      [filename]: clientId
    }));
  };

  const handleClearClientSelection = (filename) => {
    setClientSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[filename];
      return newSelections;
    });
  };

  const handleDuplicateAction = (filename, action) => {
    setDuplicateActions(prev => ({
      ...prev,
      [filename]: action // 'update' or 'skip'
    }));
  };

  const handleUpdateAllDuplicates = (shouldUpdate) => {
    setUpdateExisting(shouldUpdate);
    // When toggling global, clear individual selections
    if (shouldUpdate) {
      // Set all duplicates to 'update'
      const newActions = {};
      previewData?.parsed?.forEach(item => {
        if (item.is_duplicate && item.matched_client) {
          newActions[item.filename] = 'update';
        }
      });
      setDuplicateActions(newActions);
    } else {
      // Set all duplicates to 'skip'
      const newActions = {};
      previewData?.parsed?.forEach(item => {
        if (item.is_duplicate && item.matched_client) {
          newActions[item.filename] = 'skip';
        }
      });
      setDuplicateActions(newActions);
    }
  };

  const downloadSkippedReport = async () => {
    if (!importResults?.job_id) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API}/api/legacy-import/jobs/${importResults.job_id}/skipped-csv`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `skipped_notes_${importResults.job_id.slice(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'progress_note':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'mdt_chart_review':
        return <FileText className="h-4 w-4 text-amber-500" />;
      case 'ihbs_progress_note':
        return <FileText className="h-4 w-4 text-cyan-500" />;
      case 'cbhi_progress_note':
        return <FileText className="h-4 w-4 text-teal-500" />;
      case 'group_progress_note':
        return <Users className="h-4 w-4 text-indigo-500" />;
      case 'family_consultation':
        return <Users className="h-4 w-4 text-purple-500" />;
      case 'discharge_transfer':
        return <FileText className="h-4 w-4 text-orange-500" />;
      default:
        return <FileQuestion className="h-4 w-4 text-gray-400" />;
    }
  };

  const getNoteTypeBadge = (type) => {
    switch (type) {
      case 'progress_note':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Progress Note</Badge>;
      case 'mdt_chart_review':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">MDT Chart Review</Badge>;
      case 'ihbs_progress_note':
        return <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">IHBS Progress Note</Badge>;
      case 'cbhi_progress_note':
        return <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">CBHI Progress Note</Badge>;
      case 'group_progress_note':
        return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Group Progress Note</Badge>;
      case 'family_consultation':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Collateral Contact</Badge>;
      case 'discharge_transfer':
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Discharge/Transfer</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-50 text-gray-500">Unknown</Badge>;
    }
  };

  // Render upload step
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
          transition-colors duration-200
          ${isDragActive 
            ? 'border-teal-500 bg-teal-50' 
            : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
          }
        `}
      >
        <input {...getInputProps()} />
        <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-700">
          {isDragActive ? 'Drop files here...' : 'Drag & drop .txt files here'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          or click to select files
        </p>
      </div>

      {files.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Selected Files ({files.length} / {MAX_FILES_PER_UPLOAD})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm truncate max-w-md">{file.name}</span>
                      <span className="text-xs text-gray-400">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button 
          onClick={handlePreview}
          disabled={files.length === 0 || loading}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Parsing...
            </>
          ) : (
            <>
              Parse & Preview
              <ChevronDown className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Render preview step
  const renderPreviewStep = () => {
    if (!previewData) return null;

    const { summary, parsed } = previewData;
    const readyToImport = parsed.filter(p => p.parsed_successfully && p.matched_client);
    const needsSelection = parsed.filter(p => p.parsed_successfully && !p.matched_client && p.potential_matches?.length > 0);
    const userSelected = parsed.filter(p => p.parsed_successfully && !p.matched_client && clientSelections[p.filename]);
    const cannotImport = parsed.filter(p => !p.parsed_successfully || (!p.matched_client && (!p.potential_matches || p.potential_matches.length === 0) && !clientSelections[p.filename]));

    // Total ready = auto-matched + user-selected
    const totalReady = readyToImport.length + Object.keys(clientSelections).length;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.progress_notes}</p>
                  <p className="text-xs text-gray-500">Progress Notes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.family_consultations}</p>
                  <p className="text-xs text-gray-500">Family Consults</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.clients_matched}</p>
                  <p className="text-xs text-gray-500">Auto-Matched</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.clients_not_found}</p>
                  <p className="text-xs text-gray-500">Need Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Unknown Document Types Alert */}
        {previewData.unknown_types && Object.keys(previewData.unknown_types).length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <FileQuestion className="h-5 w-5 text-amber-600" />
                Unknown Document Types ({summary.unknown} files)
              </CardTitle>
              <CardDescription className="text-amber-700">
                These document types don't have parsers configured. Copy a sample to share for parser setup.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(previewData.unknown_types).map(([header, data], index) => (
                  <Collapsible key={index}>
                    <CollapsibleTrigger 
                      className="w-full"
                      onClick={() => toggleExpanded(`unknown-${index}`)}
                    >
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileQuestion className="h-4 w-4 text-amber-500" />
                          <div className="text-left">
                            <p className="font-medium text-amber-900">{header}</p>
                            <p className="text-xs text-amber-600">
                              {data.count} file{data.count !== 1 ? 's' : ''} • Sample: {data.sample_filename}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                            No Parser
                          </Badge>
                          {expandedItems[`unknown-${index}`] ? (
                            <ChevronUp className="h-4 w-4 text-amber-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">Sample Content (first 5000 chars)</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(data.sample_content);
                              toast.success('Sample content copied to clipboard');
                            }}
                          >
                            Copy Sample
                          </Button>
                        </div>
                        <pre className="text-xs bg-gray-50 p-3 rounded border max-h-64 overflow-auto whitespace-pre-wrap font-mono">
                          {data.sample_content}
                        </pre>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ready to Import */}
        {readyToImport.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Auto-Matched ({readyToImport.length})
                    {summary.duplicates > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 ml-2">
                        {summary.duplicates} duplicates
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    These notes have matched clients and will be imported
                  </CardDescription>
                </div>
                {summary.duplicates > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateAllDuplicates(true)}
                      className={updateExisting ? "bg-blue-100 border-blue-300" : ""}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Update All Duplicates
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateAllDuplicates(false)}
                      className={!updateExisting ? "bg-gray-100" : ""}
                    >
                      Skip All Duplicates
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {readyToImport.map((item, index) => (
                    <Collapsible key={index}>
                      <CollapsibleTrigger 
                        className="w-full"
                        onClick={() => toggleExpanded(`ready-${index}`)}
                      >
                        <div className={`flex items-center justify-between p-3 rounded hover:bg-green-100 transition-colors ${
                          item.is_duplicate ? 'bg-amber-50 border border-amber-200' : 'bg-green-50'
                        }`}>
                          <div className="flex items-center gap-3">
                            {getNoteTypeIcon(item.note_type)}
                            <span className="text-sm font-medium truncate max-w-xs">
                              {item.filename}
                            </span>
                            {getNoteTypeBadge(item.note_type)}
                            {item.is_duplicate && (
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                                Duplicate
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              → {item.matched_client?.name}
                            </span>
                            {item.is_duplicate && (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateAction(item.filename, 'update'); }}
                                  className={`px-2 py-1 text-xs rounded ${
                                    duplicateActions[item.filename] === 'update' 
                                      ? 'bg-blue-500 text-white' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  Update
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateAction(item.filename, 'skip'); }}
                                  className={`px-2 py-1 text-xs rounded ${
                                    duplicateActions[item.filename] === 'skip' || !duplicateActions[item.filename]
                                      ? 'bg-gray-500 text-white' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  Skip
                                </button>
                              </div>
                            )}
                            {expandedItems[`ready-${index}`] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 bg-gray-50 rounded-b text-sm space-y-1">
                          <p><strong>Client:</strong> {item.client_first_name} {item.client_last_name}</p>
                          <p><strong>DOB:</strong> {item.client_dob}</p>
                          <p><strong>Date of Service:</strong> {item.date_of_service || 'N/A'}</p>
                          <p><strong>Provider:</strong> {item.provider_name || 'Unknown'} 
                            {item.matched_provider && <span className="text-green-600"> (Matched: {item.matched_provider.name})</span>}
                          </p>
                          <p><strong>Location:</strong> {item.location || 'N/A'}</p>
                          {item.is_duplicate && item.existing_note && (
                            <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                              <p className="font-medium text-amber-700">Existing Note Found:</p>
                              <p className="text-xs text-amber-600">
                                {item.existing_note.doc_number && `${item.existing_note.doc_number} • `}
                                DOS: {item.existing_note.date_of_service || 'N/A'} • 
                                File: {item.existing_note.legacy_filename || 'N/A'}
                              </p>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Potential Matches - Needs User Selection */}
        {needsSelection.length > 0 && (
          <Card className="border-blue-200">
            <CardHeader className="pb-3 bg-blue-50 rounded-t-lg">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Potential Matches - Please Review ({needsSelection.length})
              </CardTitle>
              <CardDescription>
                These notes have potential client matches. Select the correct client to import.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <ScrollArea className="h-96">
                <div className="space-y-4">
                  {needsSelection.map((item, index) => {
                    const isSelected = clientSelections[item.filename];
                    const selectedClient = isSelected && item.potential_matches?.find(pm => pm.id === isSelected);
                    
                    return (
                      <div key={index} className={`border rounded-lg overflow-hidden ${isSelected ? 'border-green-300 bg-green-50' : 'border-blue-200'}`}>
                        <div className="p-3 bg-blue-50 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getNoteTypeIcon(item.note_type)}
                              <span className="text-sm font-medium">{item.filename}</span>
                              {getNoteTypeBadge(item.note_type)}
                            </div>
                            {isSelected && (
                              <Badge className="bg-green-500">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Selected: {selectedClient?.name}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Looking for: <strong>{item.client_first_name} {item.client_last_name}</strong> (DOB: {item.client_dob || 'N/A'})
                          </p>
                        </div>
                        <div className="p-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Select the correct client:</p>
                          <div className="space-y-2">
                            {item.potential_matches.map((match, matchIndex) => (
                              <div 
                                key={matchIndex}
                                onClick={() => handleSelectClient(item.filename, match.id)}
                                className={`p-3 rounded border cursor-pointer transition-all ${
                                  clientSelections[item.filename] === match.id 
                                    ? 'border-green-500 bg-green-100' 
                                    : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-sm">{match.name}</p>
                                    <p className="text-xs text-gray-500">DOB: {match.dob || 'N/A'}</p>
                                  </div>
                                  <div className="text-right">
                                    <Badge variant="outline" className={`text-xs ${match.match_score >= 70 ? 'bg-green-100 text-green-700' : match.match_score >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100'}`}>
                                      {match.match_score}% match
                                    </Badge>
                                    <p className="text-xs text-gray-400 mt-1">{match.match_reason}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {isSelected && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearClientSelection(item.filename);
                                }}
                                className="text-xs text-gray-500"
                              >
                                Clear selection
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Cannot Import */}
        {cannotImport.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Will Be Skipped ({cannotImport.length})
              </CardTitle>
              <CardDescription>
                These notes cannot be imported (no matching clients or parse errors)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {cannotImport.map((item, index) => (
                    <Collapsible key={index}>
                      <CollapsibleTrigger 
                        className="w-full"
                        onClick={() => toggleExpanded(`skip-${index}`)}
                      >
                        <div className="flex items-center justify-between p-3 bg-amber-50 rounded hover:bg-amber-100 transition-colors">
                          <div className="flex items-center gap-3">
                            {getNoteTypeIcon(item.note_type)}
                            <span className="text-sm font-medium truncate max-w-xs">
                              {item.filename}
                            </span>
                            {!item.parsed_successfully ? (
                              <Badge variant="destructive">Parse Error</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                No Matches Found
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {expandedItems[`skip-${index}`] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 bg-gray-50 rounded-b text-sm space-y-1">
                          <p><strong>Client:</strong> {item.client_first_name} {item.client_last_name}</p>
                          <p><strong>DOB:</strong> {item.client_dob || 'N/A'}</p>
                          {item.errors && item.errors.length > 0 && (
                            <div className="mt-2">
                              <strong>Errors:</strong>
                              <ul className="list-disc list-inside text-red-600">
                                {item.errors.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => setStep('upload')}>
            ← Back to Upload
          </Button>
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleImport}
              disabled={totalReady === 0 || loading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  Import {totalReady} Notes
                  <CheckCircle className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render results step
  const renderResultsStep = () => {
    if (!importResults) return null;

    const { imported, updated, skipped, failed, imported_notes, updated_notes, skipped_files, failed_files } = importResults;

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4 text-center">
              <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-3xl font-bold text-green-700">{imported}</p>
              <p className="text-sm text-green-600">Imported</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-4 text-center">
              <RefreshCw className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-3xl font-bold text-blue-700">{updated || 0}</p>
              <p className="text-sm text-blue-600">Updated</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4 text-center">
              <AlertCircle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
              <p className="text-3xl font-bold text-amber-700">{skipped}</p>
              <p className="text-sm text-amber-600">Skipped</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4 text-center">
              <XCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
              <p className="text-3xl font-bold text-red-700">{failed}</p>
              <p className="text-sm text-red-600">Failed</p>
            </CardContent>
          </Card>
        </div>

        {/* Download skipped report */}
        {(skipped > 0 || failed > 0) && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Download Skipped/Failed Report</p>
                  <p className="text-sm text-gray-500">
                    CSV file with details of {skipped + failed} files that were not imported
                  </p>
                </div>
                <Button variant="outline" onClick={downloadSkippedReport}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unknown Document Types */}
        {importResults.unknown_types && Object.keys(importResults.unknown_types).length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <FileQuestion className="h-5 w-5 text-amber-600" />
                Unknown Document Types (No Parser Configured)
              </CardTitle>
              <CardDescription className="text-amber-700">
                These files couldn't be parsed because no parser is configured for their document type.
                Copy a sample to share for parser setup.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(importResults.unknown_types).map(([header, data], index) => (
                  <Collapsible key={index}>
                    <CollapsibleTrigger 
                      className="w-full"
                      onClick={() => toggleExpanded(`result-unknown-${index}`)}
                    >
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileQuestion className="h-4 w-4 text-amber-500" />
                          <div className="text-left">
                            <p className="font-medium text-amber-900">{header}</p>
                            <p className="text-xs text-amber-600">
                              {data.count} file{data.count !== 1 ? 's' : ''} • Sample: {data.sample_filename}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                            No Parser
                          </Badge>
                          {expandedItems[`result-unknown-${index}`] ? (
                            <ChevronUp className="h-4 w-4 text-amber-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">Sample Content (first 5000 chars)</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(data.sample_content);
                              toast.success('Sample content copied to clipboard');
                            }}
                          >
                            Copy Sample
                          </Button>
                        </div>
                        <pre className="text-xs bg-gray-50 p-3 rounded border max-h-64 overflow-auto whitespace-pre-wrap font-mono">
                          {data.sample_content}
                        </pre>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Imported Notes */}
        {imported_notes && imported_notes.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Successfully Imported
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {imported_notes.map((note, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        {getNoteTypeIcon(note.note_type)}
                        <span className="text-sm">{note.client_name}</span>
                        <span className="text-xs text-gray-400">{note.date_of_service}</span>
                        {note.doc_number && <span className="text-xs text-blue-500">{note.doc_number}</span>}
                      </div>
                      {getNoteTypeBadge(note.note_type)}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Updated Notes */}
        {updated_notes && updated_notes.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-500" />
                Updated Notes ({updated_notes.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {updated_notes.map((note, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <div className="flex items-center gap-2">
                        {getNoteTypeIcon(note.note_type)}
                        <span className="text-sm">{note.client_name}</span>
                        <span className="text-xs text-gray-400">{note.date_of_service}</span>
                        {note.doc_number && <span className="text-xs text-blue-500">{note.doc_number}</span>}
                      </div>
                      {getNoteTypeBadge(note.note_type)}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Skipped Files */}
        {skipped_files && skipped_files.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Skipped Files ({skipped_files.length})
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={downloadSkippedReport}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {skipped_files.map((file, index) => (
                    <div key={index} className="p-2 bg-amber-50 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate max-w-md">{file.filename}</span>
                        <Badge variant="outline" className="bg-amber-100">{file.reason}</Badge>
                      </div>
                      {file.client_name && (
                        <p className="text-xs text-gray-500 mt-1">
                          Client: {file.client_name} {file.client_dob ? `| DOB: ${file.client_dob}` : ''} {file.date_of_service ? `| DOS: ${file.date_of_service}` : ''}
                        </p>
                      )}
                      {file.message && (
                        <p className="text-xs text-amber-700 mt-1 italic">{file.message}</p>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={clearAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Import More Files
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Legacy Note Import</h1>
        <p className="text-gray-500 mt-1">
          Import notes from your previous EMR system
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-4 mb-8">
        <div className={`flex items-center gap-2 ${step === 'upload' ? 'text-teal-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'upload' ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>1</div>
          <span className="font-medium">Upload</span>
        </div>
        <div className="flex-1 h-0.5 bg-gray-200" />
        <div className={`flex items-center gap-2 ${step === 'preview' ? 'text-teal-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'preview' ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>2</div>
          <span className="font-medium">Preview</span>
        </div>
        <div className="flex-1 h-0.5 bg-gray-200" />
        <div className={`flex items-center gap-2 ${step === 'results' ? 'text-teal-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'results' ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>3</div>
          <span className="font-medium">Results</span>
        </div>
      </div>

      {/* Step content */}
      {step === 'upload' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'results' && renderResultsStep()}
    </div>
  );
};

export default LegacyNoteImport;
